import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import OperatorSidebar from './OperatorSidebar';
import OperatorPanel from './OperatorPanel';
import OperatorOverviewPage from './OperatorOverviewPage';
import OperatorControlPlanePage from './OperatorControlPlanePage';
import SecurityPage from './SecurityPage';
import EmailSettingsPage from '../settings/components/EmailSettingsPage';
import SystemSettingsPage from '../settings/components/SystemSettingsPage';
import ClickHouseNodesPage from './ClickHouseNodesPage';
import AuditLogPage from './AuditLogPage';
import LicenseManagementPage from '../settings/components/LicenseManagementPage';
import GatewayManagementPage from './GatewayManagementPage';
import SyntheticCloudAgentsPage from './SyntheticCloudAgentsPage';
import OperatorDownloadsPage from './OperatorDownloadsPage';
import RumPlatformSettingsPage from '../settings/components/RumPlatformSettingsPage';
import SubscriptionPlansPage from '../settings/components/SubscriptionPlansPage';
import OperatorWorkbooksPage from './OperatorWorkbooksPage';
import OperatorFeatureAccessPage from './OperatorFeatureAccessPage';
import WaitlistPage from './WaitlistPage';
import OperatorContactInboxPage from './OperatorContactInboxPage';
import OperatorStripeSettingsPage from './OperatorStripeSettingsPage';
import OperatorPlansBillingPage from './OperatorPlansBillingPage';
import OperatorAiKeysPage from './OperatorAiKeysPage';
import OperatorContainerLogsPage from './OperatorContainerLogsPage';
import OperatorCveFeedPage from './OperatorCveFeedPage';

const OperatorLayout = () => {
    const { isSuperAdmin, isWebAdmin } = useAuth();

    // WEB_ADMIN sees only Website Controls. The fallback Navigate
    // (catch-all "*" at the bottom) sends them to /operator/waitlist
    // by default; we also strip Platform Controls routes so even
    // typing the URL falls back to the website surface.
    const webOnly = isWebAdmin && !isSuperAdmin;
    const defaultLanding = webOnly ? 'waitlist' : 'organizations';

    return (
        <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <OperatorSidebar />
            <Box component="main" sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
                <Routes>
                    {/* Website Controls — shared between SUPER_ADMIN and WEB_ADMIN */}
                    <Route path="waitlist" element={<WaitlistPage />} />
                    <Route path="contact-inbox" element={<OperatorContactInboxPage />} />

                    {/* Platform Controls — SUPER_ADMIN only. We render
                        the routes only when allowed; otherwise the
                        catch-all bounces the user to their landing. */}
                    {!webOnly && <Route path="overview" element={<OperatorOverviewPage />} />}
                    {!webOnly && <Route path="control-plane" element={<OperatorControlPlanePage />} />}
                    {!webOnly && <Route path="organizations" element={<OperatorPanel />} />}
                    {/* Consolidated Plans & Billing (plans + Stripe + signup). */}
                    {!webOnly && <Route path="plans-billing" element={<OperatorPlansBillingPage />} />}
                    {!webOnly && <Route path="ai-keys" element={<OperatorAiKeysPage />} />}
                    {/* Back-compat: old split routes now redirect into the consolidated page. */}
                    {!webOnly && <Route path="subscription-plans" element={<Navigate to="/operator/plans-billing" replace />} />}
                    {!webOnly && <Route path="rum-sdk" element={
                        <Box sx={{ p: 3 }}>
                            <RumPlatformSettingsPage />
                        </Box>
                    } />}
                    {!webOnly && <Route path="security" element={<SecurityPage />} />}
                    {!webOnly && <Route path="email" element={
                        <Box sx={{ p: 3 }}>
                            <EmailSettingsPage />
                        </Box>
                    } />}
                    {!webOnly && <Route path="system" element={
                        <Box sx={{ p: 3 }}>
                            <SystemSettingsPage />
                        </Box>
                    } />}
                    {!webOnly && <Route path="clickhouse-nodes" element={<ClickHouseNodesPage />} />}
                    {!webOnly && <Route path="license" element={
                        <Box sx={{ p: 3 }}>
                            <LicenseManagementPage />
                        </Box>
                    } />}
                    {!webOnly && <Route path="workbooks" element={<OperatorWorkbooksPage />} />}
                    {!webOnly && <Route path="feature-access" element={<OperatorFeatureAccessPage />} />}
                    {!webOnly && <Route path="cloud-agents" element={<SyntheticCloudAgentsPage />} />}
                    {!webOnly && <Route path="gateways" element={<GatewayManagementPage />} />}
                    {!webOnly && <Route path="downloads" element={<OperatorDownloadsPage />} />}
                    {!webOnly && <Route path="audit" element={<AuditLogPage />} />}
                    {!webOnly && <Route path="stripe" element={<Navigate to="/operator/plans-billing" replace />} />}
                    {!webOnly && <Route path="container-logs" element={<OperatorContainerLogsPage />} />}
                    {!webOnly && <Route path="cve-feed" element={<OperatorCveFeedPage />} />}

                    <Route path="*" element={<Navigate to={defaultLanding} replace />} />
                </Routes>
            </Box>
        </Box>
    );
};

export default OperatorLayout;
